import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { List } from 'antd';
import styled from 'styled-components';

import ErrorTypeEnum from 'redpoints-model-js/enumeration/ErrorTypeEnum';
import LinkTypeEnum from 'redpoints-model-js/enumeration/LinkTypeEnum';

import HttpStatus from 'redpoints-connector-common/enumeration/HttpStatus';

import {
  Alert,
  Button,
  Col,
  Row,
  Typography,
} from 'redpoints-front-components';

import {
  linksManagementClear,
  linksManagementClearRelaunchEnforcement,
  linksManagementLinksSetContentLinksUrls,
  linksManagementLinksSetDetectionLinksUrls,
} from '../../../actions/linksManagementActions';
import { getLinksManagementRelaunchEnforcementErrors } from '../../../reducers/tasks/linksManagement';
import { translate } from '../../../helpers/languageHelper';
import { getFailedRelaunchEnforcedLinks } from './linksManagementHelpers';

const AlertStyled = styled(Alert)`
  line-break: normal;
  width: 250px;
`;

const RELAUNCH_ENFORCEMENT_ALLOWED_ERROR_TYPES = Object.freeze([
  ErrorTypeEnum.RELAUNCHENFORCEMENTLINK_CONFLICT_CONTEXT,
  ErrorTypeEnum.RELAUNCHENFORCEMENTLINK_CONFLICT_PENDING_ENFORCEMENT,
  ErrorTypeEnum.RELAUNCHENFORCEMENTLINK_CONFLICT_LINK_TYPE,
  ErrorTypeEnum.RELAUNCHENFORCEMENTLINK_CONFLICT_ON_ENFORCEMENT,
  ErrorTypeEnum.RELAUNCHENFORCEMENTLINK_CONFLICT_DOMAIN,
]);

const getAlertData = (status, error) => {
  if (status === HttpStatus.NO_CONTENT.value && !error) {
    return {
      alertType: 'success',
      message: translate(`tasks.links_management.messages.success`),
      icon: <CheckCircleOutlined />,
    };
  }

  const relaunchEnforcementError = RELAUNCH_ENFORCEMENT_ALLOWED_ERROR_TYPES.find(
    errorType => error?.code === errorType.value
  );
  const errorMessage = relaunchEnforcementError
    ? translate(
        `tasks.links_management.messages.${relaunchEnforcementError.literalKey}`
      )
    : translate('error.something_wrong');

  return {
    alertType: 'warning',
    message: errorMessage,
    icon: <WarningOutlined />,
  };
};

const RelaunchEnforcementItemLinkRow = ({ linkUrl, error, status }) => {
  const { alertType, icon, message } = getAlertData(status, error);

  return (
    <List.Item>
      <List.Item.Meta description={<span title={linkUrl}>{linkUrl}</span>} />
      <AlertStyled icon={icon} message={message} showIcon type={alertType} />
    </List.Item>
  );
};

const LinksManagementRelaunchEnforcementErrors = ({ form }) => {
  const dispatch = useDispatch();

  const relaunchEnforcementErrors = useSelector(
    getLinksManagementRelaunchEnforcementErrors
  );

  const detectionLinksErrors = relaunchEnforcementErrors[
    LinkTypeEnum.DETECTION.name
  ]
    ? Object.values(relaunchEnforcementErrors[LinkTypeEnum.DETECTION.name])
    : [];
  const contentLinksErrors = relaunchEnforcementErrors[
    LinkTypeEnum.CONTENT.name
  ]
    ? Object.values(relaunchEnforcementErrors[LinkTypeEnum.CONTENT.name])
    : [];

  const handleClear = () => {
    form.resetFields();
    dispatch(linksManagementClear());
  };

  const handleEditList = () => {
    const failedLinkUrls = getFailedRelaunchEnforcedLinks(
      relaunchEnforcementErrors
    );

    form.setFieldsValue({
      [LinkTypeEnum.DETECTION.name]:
        failedLinkUrls[LinkTypeEnum.DETECTION.name],
      [LinkTypeEnum.CONTENT.name]: failedLinkUrls[LinkTypeEnum.CONTENT.name],
    });
    dispatch(
      linksManagementLinksSetDetectionLinksUrls({
        detectionLinkUrls: failedLinkUrls[LinkTypeEnum.DETECTION.name],
      })
    );
    dispatch(
      linksManagementLinksSetContentLinksUrls({
        contentLinkUrls: failedLinkUrls[LinkTypeEnum.CONTENT.name],
      })
    );

    dispatch(linksManagementClearRelaunchEnforcement());
  };

  const ItemLinkRow = ({ error, response, status }) => (
    <RelaunchEnforcementItemLinkRow
      error={error}
      linkUrl={response}
      status={status}
    />
  );

  return (
    <section className="links-management-relaunch-enforcement-errors">
      <div className="detection-error-list">
        <Typography blockTypes={['label']} className="rp-label-text">
          {translate('tasks.links_management.lists.labels.detection')}
        </Typography>
        <List
          bordered
          dataSource={detectionLinksErrors}
          renderItem={ItemLinkRow}
        />
      </div>
      <div className="content-error-list">
        <Typography blockTypes={['label']} className="rp-label-text">
          {translate('tasks.links_management.lists.labels.content')}
        </Typography>
        <List
          bordered
          dataSource={contentLinksErrors}
          renderItem={ItemLinkRow}
        />
      </div>
      <Row gutter={16} justify="end">
        <Col>
          <Button type="primary" size="fit" onClick={handleEditList}>
            {translate('tasks.links_management.actions.editList')}
          </Button>
        </Col>
        <Col>
          <Button type="secondary" onClick={handleClear} size="fit">
            {translate('tasks.links_management.actions.clear')}
          </Button>
        </Col>
      </Row>
    </section>
  );
};

export default LinksManagementRelaunchEnforcementErrors;
